import dayjs from "dayjs";
import { DaysLeftResult } from "./getDaysLeft";

export interface PeriodCycleResult extends DaysLeftResult {
  isActive: boolean;   // true while inside the active period window
  dayOfCycle: number;  // 1-indexed day within the current cycle
  cycleLengthDays: number;
  periodDays: number;
}

const pad = (n: number): string => String(n).padStart(2, "0");

/**
 * Recurring period cycle: begins on `startDateStr` (e.g. "2026-08-04") and
 * repeats every `cycleLengthDays`, lasting `periodDays` each time.
 * While active, countdown = time left until the period ends.
 * While inactive, countdown = time left until the next period starts.
 */
export const getPeriodCycleInfo = (
  startDateStr: string,
  cycleLengthDays = 28,
  periodDays = 7
): PeriodCycleResult => {
  const now = dayjs();
  const start = dayjs(startDateStr).startOf("day");

  const daysSinceStart = now.diff(start, "day");
  let dayOfCycle = daysSinceStart % cycleLengthDays;
  if (dayOfCycle < 0) dayOfCycle += cycleLengthDays;

  const isActive = dayOfCycle < periodDays;

  // Boundary of the current phase: end of period if active, else start of next period.
  const cyclesElapsed = Math.floor((daysSinceStart - dayOfCycle) / cycleLengthDays);
  const currentCycleStart = start.add(cyclesElapsed * cycleLengthDays, "day");
  const target = isActive
    ? currentCycleStart.add(periodDays, "day")
    : currentCycleStart.add(cycleLengthDays, "day");

  const totalSeconds = Math.max(0, target.diff(now, "second"));
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const totalHours = days * 24 + hours;

  const countdown = `${totalHours}:${pad(minutes)}:${pad(seconds)}`;
  const message = totalSeconds === 0 ? "That's now!" : `${countdown} left`;

  return {
    days,
    hours,
    minutes,
    seconds,
    totalHours,
    countdown,
    message,
    isActive,
    dayOfCycle: dayOfCycle + 1,
    cycleLengthDays,
    periodDays,
  };
};

/** Progress: fills through the active period, then fills through the wait until next. */
export const getPeriodProgressPercent = (r: PeriodCycleResult): number => {
  const totalDaySpan = r.isActive ? r.periodDays : r.cycleLengthDays - r.periodDays;
  const totalSeconds = totalDaySpan * 86400;
  const remainingSeconds = r.days * 86400 + r.hours * 3600 + r.minutes * 60 + r.seconds;
  return Math.round(((totalSeconds - remainingSeconds) / totalSeconds) * 100);
};